/**
 * VeyroCam — Recently viewed
 *
 * Records product handles in localStorage and renders the list by fetching
 * each product's card markup from Shopify's Section Rendering API.
 *
 * Why client-side
 * ---------------
 * The list is personal to the visitor. Rendering it server-side would put
 * one shopper's browsing history into Shopify's shared page cache, where the
 * next visitor could be served it.
 *
 * Privacy
 * -------
 * Only product handles are stored, only in localStorage, capped at 12
 * entries, and never transmitted anywhere except back to this store as an
 * ordinary product URL.
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'veyrocam:recently-viewed';
  var MAX_STORED = 12;

  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (error) {
      // Private browsing, disabled storage, or corrupt JSON. The feature is
      // non-essential — degrade to "no history" rather than throwing.
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) {
      /* Storage full or unavailable — nothing to do. */
    }
  }

  function record(handle) {
    if (!handle) return;
    var list = read().filter(function (item) {
      return item !== handle;
    });
    list.unshift(handle);
    write(list.slice(0, MAX_STORED));
  }

  /**
   * Fetch a single product's card markup.
   *
   * `?section_id=` is Shopify's Section Rendering API: it returns just that
   * section's HTML for the given product, which keeps the card's markup and
   * styling identical to every other product card without duplicating the
   * template in JavaScript.
   */
  function fetchCard(handle) {
    return fetch('/products/' + handle + '?section_id=product-card-ajax')
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelector('.vc-product-card');
      })
      .catch(function () {
        // A deleted or unpublished product 404s. Skip it silently — an error
        // here must not empty the whole row.
        return null;
      });
  }

  function render(section) {
    var grid = section.querySelector('[data-recently-viewed-grid]');
    if (!grid) return;

    var limit = parseInt(section.dataset.limit, 10) || 4;
    var current = section.dataset.currentHandle;

    var handles = read()
      .filter(function (handle) {
        return handle !== current;
      })
      .slice(0, limit);

    if (!handles.length) return;

    Promise.all(handles.map(fetchCard)).then(function (cards) {
      var found = cards.filter(Boolean);
      if (!found.length) return;

      found.forEach(function (card) {
        grid.appendChild(card);
      });

      section.hidden = false;
    });
  }

  function init() {
    var section = document.querySelector('[data-recently-viewed]');

    // Record the current product before rendering, then exclude it from the
    // list — a shopper does not need to be shown the page they are on.
    var handleMeta = section && section.dataset.currentHandle;
    if (handleMeta) record(handleMeta);

    if (section) render(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
